// View state (§10): zoom, tools, before/after, clipping overlay.
import { create } from 'zustand';

export type Tool =
  | 'none'
  | 'crop'
  | 'straighten'
  | 'wbPicker'
  | 'mask'
  | 'heal'
  | 'skin'
  | 'cleanWhites'
  | 'bgKey'
  | 'bgRefine'
  | 'spot' // Simple mode: tap a spot to heal it
  | 'cropPan'; // Simple mode: drag the photo inside the crop frame

/** Brush settings shared by the mask brush, heal, skin and clean whites (size = fraction of the short edge). */
export type Brush = { size: number; feather: number; flow: number };

export type UiState = {
  zoom: 'fit' | 1;
  /** Pan offset in output pixels at 100% (top-left of the viewport). */
  pan: { x: number; y: number };
  tool: Tool;
  before: boolean; // `\` toggle or hold
  split: boolean; // Y
  splitPos: number; // 0..1 across the viewer
  clipping: boolean; // J
  dragging: boolean; // a slider is being dragged → half-res preview
  exportOpen: boolean; // Ctrl+E
  maskId: string | null; // selected local mask
  maskOverlay: boolean; // O
  brush: Brush;
  healMode: 'heal' | 'clone';
  /** Alt-click heal source for the next spot (source-normalized), or null = auto-pick. */
  healSource: { x: number; y: number } | null;
  /** §11.12 view while the background is removed: 1 checker, 2 white, 3 black, 4 mask overlay, 5 matte (W). */
  bgView: number;
  /** Refine brush: keep (add subject) or remove; Alt inverts. */
  refineMode: 'keep' | 'remove';
  cheatOpen: boolean; // ?
  aboutOpen: boolean;
  set: (p: Partial<Omit<UiState, 'set'>>) => void;
};

export const useUi = create<UiState>((set) => ({
  zoom: 'fit',
  pan: { x: 0, y: 0 },
  tool: 'none',
  before: false,
  split: false,
  splitPos: 0.5,
  clipping: false,
  dragging: false,
  exportOpen: false,
  maskId: null,
  maskOverlay: false,
  brush: { size: 0.05, feather: 0.5, flow: 1 },
  healMode: 'heal',
  healSource: null,
  bgView: 1,
  refineMode: 'keep',
  cheatOpen: false,
  aboutOpen: false,
  set: (p) => set(p),
}));
